import { useEffect, useRef, useState } from 'react'
import { ReverLoading } from '@itsrever/design-system'
import type {
  PDFDocumentProxy,
  RenderTask,
  TextLayer
} from 'pdfjs-dist/legacy/build/pdf.mjs'
import pdfjsUrl from 'pdfjs-dist/legacy/build/pdf.min.mjs?url'
import PdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'
import './pdf-viewer.css'

const PAGE_WIDTH_PX = 900

type PdfJs = typeof import('pdfjs-dist/legacy/build/pdf.mjs')

let pdfjs: Promise<PdfJs> | undefined

// Loaded from its own file at runtime: vite.config inlines every static and dynamic import into the main bundle.
function loadPdfJs(): Promise<PdfJs> {
  pdfjs ??= import(/* @vite-ignore */ pdfjsUrl).then((module: PdfJs) => {
    module.GlobalWorkerOptions.workerPort = new PdfWorker()
    return module
  })
  return pdfjs
}

export function PdfViewer({
  src,
  errorMessage
}: {
  src: string
  errorMessage: string
}) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let destroy: (() => void) | undefined
    let cancelled = false
    loadPdfJs()
      .then(({ getDocument }) => {
        if (cancelled) return
        const task = getDocument({ url: src })
        destroy = () => task.destroy()
        return task.promise.then(setPdf)
      })
      .catch(() => setFailed(true))
    return () => {
      cancelled = true
      destroy?.()
    }
  }, [src])

  if (failed) {
    return <p className="p-7 text-sm text-neutral-500">{errorMessage}</p>
  }

  if (!pdf) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <ReverLoading />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-4 px-4 py-6">
      {Array.from({ length: pdf.numPages }, (_, i) => (
        <PdfPage key={i} pdf={pdf} pageNumber={i + 1} />
      ))}
    </div>
  )
}

function PdfPage({
  pdf,
  pageNumber
}: {
  pdf: PDFDocumentProxy
  pageNumber: number
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const textLayerRef = useRef<HTMLDivElement>(null)
  const [aspectRatio, setAspectRatio] = useState(Math.SQRT2)
  const [isNear, setIsNear] = useState(false)

  // Every page gets its text layer up front so browser find (Cmd+F) searches the whole document.
  useEffect(() => {
    const container = containerRef.current
    const textLayerContainer = textLayerRef.current
    if (!container || !textLayerContainer) return
    let textLayer: TextLayer | undefined
    let resizeObserver: ResizeObserver | undefined
    let cancelled = false

    Promise.all([loadPdfJs(), pdf.getPage(pageNumber)])
      .then(([pdfJs, page]) => {
        if (cancelled) return
        const viewport = page.getViewport({ scale: 1 })
        setAspectRatio(viewport.height / viewport.width)
        resizeObserver = new ResizeObserver(([entry]) => {
          container.style.setProperty(
            '--total-scale-factor',
            String(entry.contentRect.width / viewport.width)
          )
        })
        resizeObserver.observe(container)
        textLayer = new pdfJs.TextLayer({
          textContentSource: page.streamTextContent(),
          container: textLayerContainer,
          viewport
        })
        return textLayer.render()
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
      resizeObserver?.disconnect()
      textLayer?.cancel()
      textLayerContainer.replaceChildren()
    }
  }, [pdf, pageNumber])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    // Only pages near the viewport hold a canvas; 62 full-size canvases would take hundreds of MB.
    const observer = new IntersectionObserver(
      ([entry]) => setIsNear(entry.isIntersecting),
      { rootMargin: '1500px 0px' }
    )
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!isNear || !canvas) return
    let renderTask: RenderTask | undefined
    let cancelled = false

    pdf.getPage(pageNumber).then((page) => {
      if (cancelled) return
      const base = page.getViewport({ scale: 1 })
      const scale = (PAGE_WIDTH_PX / base.width) * window.devicePixelRatio
      const viewport = page.getViewport({ scale })
      canvas.width = viewport.width
      canvas.height = viewport.height
      renderTask = page.render({ canvas, viewport })
      renderTask.promise.catch(() => undefined)
    })

    return () => {
      cancelled = true
      renderTask?.cancel()
      canvas.width = 0
      canvas.height = 0
    }
  }, [isNear, pdf, pageNumber])

  return (
    <div
      ref={containerRef}
      className="relative w-full bg-white shadow-sm ring-1 ring-neutral-200"
      style={{ maxWidth: PAGE_WIDTH_PX, aspectRatio: `1 / ${aspectRatio}` }}
    >
      <canvas ref={canvasRef} className="size-full" />
      <div ref={textLayerRef} className="pdf-text-layer" />
    </div>
  )
}
